import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { callTRPCProcedure } from '@trpc/server'
import { appRouter, createContext } from 'server/trpc'

type TRPCCallInput = { path: string; input: unknown; type: 'query' | 'mutation' }

// Request-scoped memo map shared by every in-process tRPC call of a SINGLE SSR document. `getRequest()`
// returns the same `Request` instance for the lifetime of one document render, so keying a WeakMap by it
// hands every call (across all the route loaders that fan out queries) the same cache — letting the auth
// middleware resolve the session + run getUserFromDB once per page instead of once per query. The
// WeakMap self-evicts when the request is GC'd, so nothing leaks between requests on the long-lived
// server. The HTTP path never reaches here (it builds its own per-request context).
const documentRequestCaches = new WeakMap<Request, Map<string, Promise<unknown>>>()

function cacheForDocumentRequest(req: Request): Map<string, Promise<unknown>> {
  let cache = documentRequestCaches.get(req)
  if (!cache) {
    cache = new Map()
    documentRequestCaches.set(req, cache)
  }
  return cache
}

// Re-derive the headers the client transport (main.tsx) adds from window/location,
// pulling them off the document request URL instead. Session cookies ride along on
// `req` untouched, so auth resolves server-side without any extra wiring.
function buildContextRequest(req: Request): Request {
  const url = new URL(req.url)
  const headers = new Headers(req.headers)

  const clientId = url.searchParams.get('clientId')
  if (clientId) headers.set('Client-id', clientId)

  const token = url.searchParams.get('token')
  if (token) headers.set('Authorization', `Bearer ${token}`)

  // Locale precedence mirrors app-store: ?lang → `language` cookie → request default. `x-locale` is the
  // EXPLICIT slot the server checks before the cookie — setting `Accept-language` here would lose to a
  // stale cookie, which is how an SDK `?lang=` link ended up with chrome and content in two languages.
  const lang = url.searchParams.get('lang')
  if (lang) headers.set('x-locale', lang)

  return new Request(req.url, { method: req.method, headers })
}

export const trpcSSRCallFn = createServerFn({ method: 'POST' })
  .validator((input: TRPCCallInput) => input)
  .handler(async ({ data }) => {
    const documentRequest = getRequest()
    const req = buildContextRequest(documentRequest)
    return callTRPCProcedure({
      router: appRouter,
      path: data.path,
      getRawInput: () => Promise.resolve(data.input),
      // Share one request-scoped memo map across every SSR tRPC call of this document (see above).
      ctx: createContext({ req, cache: cacheForDocumentRequest(documentRequest) } as Parameters<
        typeof createContext
      >[0]),
      type: data.type,
      signal: undefined,
      batchIndex: 0,
    })
  })
